import type { ReactNode } from 'react';
import { PENDING } from '../../api/client';

/**
 * A value the company has not supplied is shown as Pending Confirmation, in a
 * style that reads as an open question rather than as data. Nothing in this
 * module substitutes a plausible-looking default for a missing fact.
 */
export function Pending({ children }: { children?: ReactNode }) {
  if (children === undefined || children === null || children === '' || children === PENDING) {
    return <span className="org-pending">Pending Confirmation</span>;
  }
  return <>{children}</>;
}

/**
 * employees.date_of_joining is NOT NULL, so an unsupplied date is stored as the
 * documented sentinel. It must never be rendered as though it were a real date.
 */
export const UNKNOWN_DATE = '1900-01-01';

export function DateValue({ value }: { value?: string | null }) {
  if (!value || String(value).startsWith(UNKNOWN_DATE)) return <Pending />;
  return <span className="mono">{String(value).slice(0, 10)}</span>;
}

export function asList(value: string[] | string | undefined | null): string[] | null {
  if (Array.isArray(value)) return value;
  return null;
}

const GAP_LABELS: Record<string, string> = {
  designation: 'Designation',
  department: 'Department',
  reporting_manager: 'Reporting manager',
  work_arrangement: 'Work arrangement',
  employment_type: 'Employment type',
  date_of_joining: 'Joining date',
  job_description: 'Job description',
  kpis: 'KPIs',
};

/**
 * Severity is about what the gap blocks, not about how it looks. A missing
 * manager stops approval routing; a missing joining date does not.
 */
const GAP_SEVERITY: Record<string, 'critical' | 'high' | 'medium'> = {
  reporting_manager: 'critical',
  designation: 'critical',
  job_description: 'high',
  work_arrangement: 'high',
  department: 'medium',
  employment_type: 'medium',
  date_of_joining: 'medium',
  kpis: 'medium',
};

export const gapLabel = (key: string) => GAP_LABELS[key] ?? key.replace(/_/g, ' ');
export const gapSeverity = (key: string) => GAP_SEVERITY[key] ?? 'medium';

export function SeverityBadge({ level }: { level: 'critical' | 'high' | 'medium' }) {
  return <span className={`org-sev org-sev-${level}`}>{level}</span>;
}

export function Stat({ label, value, tone, hint }: {
  label: string; value: number | string; tone?: 'ok' | 'warn' | 'bad'; hint?: string;
}) {
  return (
    <div className={`org-stat${tone ? ` org-stat-${tone}` : ''}`}>
      <span className="org-stat-value">{value}</span>
      <span className="org-stat-label">{label}</span>
      {hint && <span className="org-stat-hint">{hint}</span>}
    </div>
  );
}

export function useAsyncError() {
  return (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');
}
